<template>
  <div>
    <form @submit.prevent="submit">
      <h2>{{ $t("global.select-a-certificate-type") }}</h2>
      <div class="ssl-packages">
        <h4>{{ $t("global.single-domain") }}</h4>
        <div v-for="pack in singleDomainPackages" :key="pack.id" class="ssl-package">
          <FormRadio
            v-if="pack.id"
            class="mr-2"
            :label="`${pack.name} ($${pack.annualFee})`"
            name="package_id"
            :errors="validation.fields.package_id.errors"
            :dirty="validation.fields.package_id.dirty"
            :value="pack.id"
            @change="packageChange"
          />
        </div>
        <h4>{{ $t("global.multi-domains") }}</h4>
        <div v-for="pack in multipleDomainPackages" :key="pack.id" class="ssl-package">
          <FormRadio
            v-if="pack.id"
            class="mr-2"
            :label="pack.name"
            name="package_id"
            :errors="validation.fields.package_id.errors"
            :dirty="validation.fields.package_id.dirty"
            :value="pack.id"
            @change="packageChange"
          />
        </div>
        <h4>{{ $t("global.wildcard") }}</h4>
        <div v-for="pack in wildcardDomainPackages" :key="pack.id" class="ssl-package">
          <FormRadio
            v-if="pack.id"
            class="mr-2"
            :label="pack.name"
            name="package_id"
            :errors="validation.fields.package_id.errors"
            :dirty="validation.fields.package_id.dirty"
            :value="pack.id"
            @change="packageChange"
          />
        </div>
      </div>
      <h2>{{ $t("global.source") }}</h2>
      <div class="ssl-source">
        <h4>{{ $t("global.do-you-have-key-and-csr") }}</h4>
        <FormRadio
          name="source"
          :value="true"
          :label="$t('global.yes-i-have-them')"
          @change="sourceChange"
        />
        <FormRadio
          name="source"
          :value="false"
          :checked="true"
          :label="$t('global.i-need-them-or-am-unsure')"
          @change="sourceChange"
        />
      </div>
      <h2>{{ $t("global.details") }}</h2>
      <div v-if="source" class="ssl-details">
        <FormTextArea
          name="key"
          v-model="fields.key"
          :label="$t('global.private-key')"
          :placeholder="$t('global.begin-private-key')"
          :rows="10"
        />
        <FormTextArea
          name="csr"
          v-model="fields.csr"
          :label="$t('global.certificate-signing-request-csr')"
          :placeholder="$t('global.begin-csr')"
          :rows="10"
        />
      </div>
      <div v-else class="ssl-details">
        <h4>{{ $t("global.enter-info-for-certificate") }}</h4>
        <FormInput
          v-if="!isMultiDomain"
          name="domain"
          v-model="fields.domain"
          :label="`${$t('global.domain')} *`"
          :errors="validation.fields.domain.errors"
          :dirty="validation.fields.domain.dirty"
          :disabled="loading"
          class="pb-2"
          @change="domainChange"
        />
        <FormTextArea
          v-else
          name="domain"
          v-model="fields.domain"
          :label="$t('global.domains')"
          :placeholder="$t('global.one-domain-per-line')"
          :errors="validation.fields.domain.errors"
          :dirty="validation.fields.domain.dirty"
          :rows="10"
          @change="domainChange"
        />
        <FormInput
          name="email"
          v-model="fields.email"
          :label="`${$t('global.email')} *`"
          :errors="validation.fields.email.errors"
          :dirty="validation.fields.email.dirty"
          :disabled="loading"
          class="pb-2"
        />
        <FormInput
          name="street"
          v-model="fields.street"
          :label="`${$t('global.address')} *`"
          :errors="validation.fields.street.errors"
          :dirty="validation.fields.street.dirty"
          :disabled="loading"
          class="pb-2"
        />
        <div class="row">
          <div class="col-md-4">
            <FormInput
              name="locality"
              v-model="fields.locality"
              :label="`${$t('global.city')} *`"
              :errors="validation.fields.locality.errors"
              :dirty="validation.fields.locality.dirty"
              :disabled="loading"
              class="pb-2"
            />
          </div>
          <div class="col-md-4">
            <FormSelect
              name="state"
              v-model="fields.state"
              :label="`${$t('global.state')} *`"
              :errors="validation.fields.state.errors"
              :dirty="validation.fields.state.dirty"
            >
              <option selected="true" value=""> - {{ $t("global.select-state") }} - </option>
              <option
                :value="state.code"
                v-for="state in states"
                :key="state.code"
                :selected="fields.state === state.code"
              >
                {{ state.name }}
              </option>
            </FormSelect>
          </div>
          <div class="col-md-4">
            <FormSelect
              name="country"
              v-model="fields.country"
              :label="`${$t('global.country')} *`"
              :errors="validation.fields.country.errors"
              :dirty="validation.fields.country.dirty"
            >
              <option selected="true" value=""> - {{ $t("global.select-country") }} - </option>
              <option
                v-for="country in countries"
                :value="country.code"
                :key="country.code"
                :selected="fields.country === country.code"
              >
                {{ country.name }}
              </option>
            </FormSelect>
          </div>
        </div>
        <div class="row">
          <div class="col-md-6">
            <FormInput
              name="organization"
              v-model="fields.organization"
              :label="`${$t('global.organization')} *`"
              :errors="validation.fields.organization.errors"
              :dirty="validation.fields.organization.dirty"
              :disabled="loading"
              class="pb-2"
            />
          </div>
          <div class="col-md-6">
            <FormInput
              name="organizational_unit"
              v-model="fields.organizational_unit"
              :label="`${$t('global.organizational-unit')} *`"
              :errors="validation.fields.organizational_unit.errors"
              :dirty="validation.fields.organizational_unit.dirty"
              :disabled="loading"
              class="pb-2"
            />
          </div>
        </div>
      </div>
      <div class="form-buttons">
        <Btn variant="primary" type="submit" :disabled="!isSubmittable()" :loading="loading">
          {{ $t("global.order") }}
        </Btn>
      </div>
    </form>
  </div>
</template>
<script>
import { computed, reactive, ref } from "@vue/composition-api";
import Btn from "@/components/display/Btn";
import { constants } from "@/utils";
import FormInput from "@/components/display/form/FormInput";
import FormRadio from "@/components/display/form/FormRadio";
import FormSelect from "@/components/display/form/FormSelect";
import FormTextArea from "@/components/display/form/FormTextArea";
import store from "@/store";
import useValidator from "@/utils/useValidator";
import { orderSsl as validationRules } from "leatherman-js/utils/validations/forms";

export default {
  name: "CertificateAddForm",
  components: {
    Btn,
    FormInput,
    FormRadio,
    FormSelect,
    FormTextArea,
  },
  props: {
    packages: {
      type: Object,
      required: true,
    },
    onChange: Function,
  },
  setup(props, { root }) {
    const singleDomainCodes = ["positivessl", "truebizid", "truebusinessidev"];
    const multipleDomainCodes = ["positivemdcssl", "truebusinessidevmd"];
    const wildcardDomainCodes = ["positivesslwildcard"];
    const countries = Object.keys(constants.COUNTRIES).map((code) => {
      return { code, name: constants.COUNTRIES[code] };
    });
    const states = Object.keys(constants.STATES).map((code) => {
      return { code, name: constants.STATES[code] };
    });

    const getPackage = (code) => props.packages.list.find((pack) => pack.sslProductCode === code);
    const isSubmittable = () => fields.package_id !== 0;

    const singleDomainPackages = computed(() => singleDomainCodes.map(getPackage));
    const multipleDomainPackages = computed(() => multipleDomainCodes.map(getPackage));
    const wildcardDomainPackages = computed(() => wildcardDomainCodes.map(getPackage));

    const loading = ref(false);
    const errors = ref(null);
    const source = ref(false);
    const fields = reactive({
      country: "",
      csr: "",
      domain: "",
      email: "",
      key: "",
      locality: "",
      organization: "",
      organizational_unit: "",
      package_id: 0,
      state: "",
      street: "",
    });

    const isMultiDomain = computed(() =>
      multipleDomainPackages.value.find((pack) => fields.package_id === pack.id)
    );

    const packageChange = (res) => {
      fields.package_id = props.packages.list.find((pack) => Number(res.value) === pack.id).id;
      props.onChange("package", fields.package_id);
    };

    const sourceChange = (res) => {
      source.value = res.value === "true";
      props.onChange("source", source.value);
    };

    const domainChange = () => {
      props.onChange("domain", fields.domain);
    };

    const { validator } = useValidator();
    const validation = validator(
      fields,
      validationRules,
      {},
      {
        country: {
          required: root.$t("validation.country-required"),
        },
        domain: {
          required: root.$t("validation.domain-required"),
        },
        email: {
          required: root.$t("validation.email-required"),
          regex: root.$t("validation.invalid-email"),
        },
        locality: {
          required: root.$t("validation.locality-required"),
        },
        organization: {
          required: root.$t("validation.organization-required"),
        },
        organizational_unit: {
          required: root.$t("validation.organizational-unit-required"),
        },
        package_id: {
          required: root.$t("validation.package-required"),
        },
        state: {
          required: root.$t("validation.state-required"),
        },
        street: {
          required: root.$t("validation.address-required"),
        },
      }
    );

    const decodeCsr = () => {
      props.onChange("csr", false);
      return store
        .dispatch("certificates/decodeCsr", { csr: fields.csr, package_id: fields.package_id })
        .then((csr) => {
          onChange("csr", csr);
          return Promise.resolve(csr);
        })
        .catch((error) => {
          store.dispatch("toasts/addToast", {
            variant: "error",
            message: root.$t("toasts.ssl-order-error-message", {
              error: error.request?.response?.[0],
            }),
          });
          throw new Error(error);
        });
    };

    const submit = () => {
      errors.value = null;
      (source.value ? decodeCsr() : validation.validate())
        .then(() => {
          loading.value = true;
          const data = {
            distinguished_name: { ...fields },
            package_id: fields.package_id,
            domains: fields.domain,
          };
          store
            .dispatch("certificates/getCsrDetails", data)
            .then(() => {
              return store.dispatch("certificates/order", data);
            })
            .then(() => {
              root.$router.push("/ssl");
              store.dispatch("toasts/addToast", {
                variant: "info",
                message: root.$t("toasts.ssl-ordering-message"),
              });
            })
            .catch((e) => {
              const errors = e.request.response;
              for (let key in errors) {
                let message = errors[key];
                if (message.length > 0) {
                  store.dispatch("toasts/addToast", {
                    variant: "error",
                    message: root.$t("toasts.ssl-order-error-message", { error: message }),
                  });
                } else {
                  store.dispatch("toasts/addToast", {
                    variant: "error",
                    message: root.$t("toasts.ssl-order-error-message", { error: "" }),
                  });
                }
              }
            })
            .finally(() => {
              loading.value = false;
            });
        })
        .catch(() => {});
    };

    return {
      countries,
      domainChange,
      errors,
      fields,
      isMultiDomain,
      isSubmittable,
      loading,
      multipleDomainPackages,
      packageChange,
      singleDomainPackages,
      source,
      sourceChange,
      states,
      submit,
      validation,
      wildcardDomainPackages,
    };
  },
};
</script>
<style lang="scss" scoped>
.ssl-package {
  width: 50%;
  display: inline-block;
}
</style>
